/**
 * Login — L5 "Gates of the city" (design/redesign_v3/Auth & Onboarding.dc.html
 * frames "Login" + "Login desktop").
 * Mobile: skyline hero on top, form below (stacked).
 * Desktop (≥900px): split view — form pane left, brand pane right
 * (lockup + tagline over a full-height arch skyline). This split layout was
 * already built + user-approved; only copy/order was adjusted to match the
 * redesign frame (primary action first, OAuth secondary below it).
 *
 * Passwordless (Stage 2): the primary flow is email → 6-digit code (POST
 * /api/auth/request-code + /verify-code, live). You enter your email here and
 * type the code on /verify-code. Google/Apple OAuth buttons are Stage 3
 * placeholders (still disabled // TODO(wire)).
 */
import { router } from 'expo-router';
import React, { useState } from 'react';
import { Linking, Pressable, ScrollView, View, useWindowDimensions } from 'react-native';

import { radius, space } from '@bero/tokens';
import { ArchSkyline, BrandLockup } from '@/components/brand';
import { homeFor } from '@/lib/homeFor';
import { useSession } from '@/lib/session';
import { googleAvailable, appleAvailable, googleIdToken, appleCredential } from '@/lib/social-auth';
import { useT } from '@/i18n';
import { useTheme } from '@/theme';
import { AppText, Button, Field, Input, Screen } from '@/ui';

const WIDE = 900;

function OAuthButton({ label, variant, style, onPress }: { label: string; variant: 'apple' | 'google'; style?: object; onPress: () => void }) {
  const { colors } = useTheme();
  const apple = variant === 'apple';
  return (
    <Pressable
      accessibilityRole="button"
      accessibilityLabel={label}
      onPress={onPress}
      style={[
        {
          height: 48, borderRadius: radius.md, flexDirection: 'row', alignItems: 'center',
          justifyContent: 'center', gap: 10,
          backgroundColor: apple ? colors.textPrimary : colors.canvas0,
          borderWidth: apple ? 0 : 1, borderColor: colors.borderFirm,
        },
        style,
      ]}
    >
      {apple ? (
        <AppText weight="600" style={{ color: colors.canvas0, fontSize: 17, lineHeight: 20 }}></AppText>
      ) : (
        <View style={{ width: 17, height: 17, borderRadius: radius.full, borderWidth: 5, borderTopColor: '#EA4335', borderRightColor: '#FBBC05', borderBottomColor: '#34A853', borderLeftColor: '#4285F4' }} />
      )}
      <AppText variant="bodyMd" weight="500" style={{ color: apple ? colors.canvas0 : colors.textPrimary }}>
        {label}
      </AppText>
    </Pressable>
  );
}

function LoginForm({ variant }: { variant: 'mobile' | 'desktop' }) {
  const { t } = useT();
  const { colors } = useTheme();
  const { requestCode, googleSignIn, appleSignIn } = useSession();
  const [email, setEmail] = useState('');
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);
  const [socialBusy, setSocialBusy] = useState(false);

  const onGoogle = async () => {
    if (busy || socialBusy) return;
    setSocialBusy(true); setError('');
    try {
      const { user } = await googleSignIn(await googleIdToken());
      router.replace(homeFor(user) as '/(tabs)/home');
    } catch (e) {
      const msg = (e as { error?: string })?.error;
      if (msg && !/cancel/i.test(msg)) setError(msg); // ignore user-cancelled
    } finally { setSocialBusy(false); }
  };

  const onApple = async () => {
    if (busy || socialBusy) return;
    setSocialBusy(true); setError('');
    try {
      const { user } = await appleSignIn(await appleCredential());
      router.replace(homeFor(user) as '/(tabs)/home');
    } catch (e) {
      if ((e as { code?: string })?.code !== 'ERR_REQUEST_CANCELED') {
        setError((e as { error?: string })?.error ?? 'Apple sign-in failed. Please try again.');
      }
    } finally { setSocialBusy(false); }
  };

  const submit = async () => {
    if (busy) return;
    const clean = email.trim().toLowerCase();
    if (!clean.includes('@')) { setError('Enter a valid email address.'); return; }
    setBusy(true);
    setError('');
    try {
      await requestCode(clean);
      router.push({ pathname: '/verify-code', params: { email: clean } });
    } catch (e) {
      setError((e as { error?: string })?.error ?? 'Could not send a code. Please try again.');
    } finally {
      setBusy(false);
    }
  };

  return (
    <>
      {variant === 'mobile' ? (
        <>
          <BrandLockup markHeight={26} />
          <AppText variant="displayLg" style={{ marginTop: space.s5 }}>
            {t('login_tagline')}
          </AppText>
          <AppText variant="bodyMd" tone="secondary" style={{ marginTop: 8 }}>
            Sign in with the email your society has on file.
          </AppText>
        </>
      ) : (
        <>
          <AppText variant="displayLg" display>{t('sign_in')}</AppText>
          <AppText variant="bodyMd" tone="secondary" style={{ marginTop: 8 }}>
            {t('login_sub')}
          </AppText>
        </>
      )}

      {/* Passwordless: enter your email → we send a 6-digit code → you verify it
          on the next screen. Google/Apple below are Stage 3 (still placeholders). */}
      <View style={{ gap: space.s3, marginTop: space.s6 }}>
        <Field label={t('email')} error={error || undefined}>
          <Input value={email} onChangeText={setEmail} autoCapitalize="none" autoComplete="email" keyboardType="email-address" placeholder="you@example.com"
            returnKeyType="go" onSubmitEditing={() => void submit()} />
        </Field>
        <Button title={t('login_continue')} size="lg" loading={busy} onPress={() => void submit()} />
      </View>

      {(googleAvailable || appleAvailable) ? (
        <>
          <View style={{ flexDirection: 'row', alignItems: 'center', gap: space.s3, marginVertical: space.s5 }}>
            <View style={{ flex: 1, height: 0.5, backgroundColor: colors.borderSoft }} />
            <AppText variant="bodySm" tone="tertiary">{t('or')}</AppText>
            <View style={{ flex: 1, height: 0.5, backgroundColor: colors.borderSoft }} />
          </View>

          {/* Sign in with Apple (iOS) + Google (iOS/Android). Verified server-side
              (/api/auth/apple, /api/auth/google). Hidden on web (email only). */}
          <View style={{ gap: 10, flexDirection: variant === 'desktop' ? 'row' : 'column' }}>
            {appleAvailable ? (
              <OAuthButton label={variant === 'desktop' ? 'Apple' : 'Continue with Apple'} variant="apple" onPress={() => void onApple()} style={variant === 'desktop' ? { flex: 1 } : undefined} />
            ) : null}
            {googleAvailable ? (
              <OAuthButton label={variant === 'desktop' ? 'Google' : 'Continue with Google'} variant="google" onPress={() => void onGoogle()} style={variant === 'desktop' ? { flex: 1 } : undefined} />
            ) : null}
          </View>
        </>
      ) : null}

      <Pressable accessibilityRole="link" onPress={() => router.push('/onboarding/create-account')} hitSlop={8} style={{ marginTop: space.s6, alignSelf: 'center' }}>
        <AppText variant="bodyMd" tone="secondary">
          New to Bero? <AppText variant="bodyMd" weight="500" tone="brand">{t('create_account')}</AppText>
        </AppText>
      </Pressable>

      {variant === 'mobile' ? (
        <AppText variant="bodySm" tone="tertiary" style={{ textAlign: 'center', fontSize: 11.5, lineHeight: 16, marginTop: space.s6 }}>
          By continuing you agree to our{' '}
          <AppText variant="bodySm" weight="500" tone="brand" style={{ fontSize: 11.5, lineHeight: 15 }} onPress={() => void Linking.openURL('https://usebero.com/privacy')}>
            Terms
          </AppText>
          {' '}and{' '}
          <AppText variant="bodySm" weight="500" tone="brand" style={{ fontSize: 11.5, lineHeight: 15 }} onPress={() => void Linking.openURL('https://usebero.com/privacy')}>
            Privacy Policy
          </AppText>
        </AppText>
      ) : null}
    </>
  );
}

export default function Login() {
  const { t } = useT();
  const { colors } = useTheme();
  const { width, height } = useWindowDimensions();

  if (width >= WIDE) {
    // Desktop split: form left, brand + skyline right.
    return (
      <View style={{ flex: 1, flexDirection: 'row', backgroundColor: colors.canvas0 }}>
        <ScrollView
          style={{ width: 480, flexGrow: 0, flexShrink: 0 }}
          contentContainerStyle={{ flexGrow: 1, justifyContent: 'center', paddingHorizontal: space.s10, paddingVertical: space.s8 }}
          showsVerticalScrollIndicator={false}
        >
          <LoginForm variant="desktop" />
        </ScrollView>

        <View style={{ flex: 1, backgroundColor: colors.canvas1, overflow: 'hidden', justifyContent: 'flex-end' }}>
          <View style={{ position: 'absolute', top: 0, left: 0, right: 0, bottom: height * 0.42, alignItems: 'center', justifyContent: 'center', gap: space.s4, zIndex: 2, paddingHorizontal: space.s10 }}>
            <BrandLockup markHeight={44} />
            <AppText variant="displayXl" style={{ textAlign: 'center' }}>{t('login_tagline')}</AppText>
            <AppText variant="bodyMd" tone="secondary" style={{ textAlign: 'center', maxWidth: 380 }}>
              Gate security, notices, payments and bookings — with no ads, ever.
            </AppText>
          </View>
          <ArchSkyline height={Math.min(height * 0.62, 620)} width={Math.max(width - 480, 520)} bare />
        </View>
      </View>
    );
  }

  // Mobile / narrow: stacked hero + form.
  return (
    <Screen scroll style={{ paddingBottom: 0 }}>
      <ArchSkyline height={230} />
      <View style={{ paddingHorizontal: space.s4, paddingTop: 22, paddingBottom: space.s6, maxWidth: 560, width: '100%', alignSelf: 'center' }}>
        <LoginForm variant="mobile" />
      </View>
    </Screen>
  );
}
